import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Flame,
  ChevronDown,
  ChevronUp,
  Users,
  Zap,
  Calculator,
  Search,
  Check,
  X,
  Settings2,
  UtensilsCrossed,
  Target,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Meal slot configuration
const MEAL_SLOTS = [
  { id: "desayuno", name: "Desayuno", time: "08:00", defaultPercent: 20 },
  { id: "media_manana", name: "Media Mañana", time: "11:00", defaultPercent: 12 },
  { id: "comida", name: "Comida", time: "14:00", defaultPercent: 30 },
  { id: "merienda", name: "Merienda", time: "17:00", defaultPercent: 12 },
  { id: "cena", name: "Cena", time: "21:00", defaultPercent: 26 },
];

// Mode options
const MODE_OPTIONS = [
  { value: "flexible", label: "Flexible", description: "Todas las comidas flexibles" },
  { value: "hybrid", label: "Híbrido", description: "Algunas fijas, otras flexibles" },
];

const AdminFlexiblePlans = () => {
  const { getAuthHeaders } = useAuth();
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberSelector, setShowMemberSelector] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    mode: "flexible",
    daily_targets: {
      calories: 2000,
      protein: 150,
      carbs: 200,
      fat: 60,
    },
    meal_slots: {},
    tolerance_percent: 5,
    notes: "",
  });

  // Initialize meal slots with default distribution
  const initializeMealSlots = useCallback((dailyTargets) => {
    const slots = {};
    MEAL_SLOTS.forEach(slot => {
      const pct = slot.defaultPercent / 100;
      slots[slot.id] = {
        name: slot.name,
        mode: "flexible",
        targets: {
          calories: Math.round(dailyTargets.calories * pct),
          protein: Math.round(dailyTargets.protein * pct),
          carbs: Math.round(dailyTargets.carbs * pct),
          fat: Math.round(dailyTargets.fat * pct),
        },
        time: slot.time,
      };
    });
    return slots;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/flexible-plans`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/members`, { headers: getAuthHeaders() }),
      ]);
      setPlans(plansRes.data.plans || []);
      setMembers(membersRes.data || []);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-distribute macros when daily targets change
  useEffect(() => {
    if (!editingPlan) {
      setFormData(prev => {
        if (Object.keys(prev.meal_slots).length === 0) {
          return {
            ...prev,
            meal_slots: initializeMealSlots(prev.daily_targets),
          };
        }
        return prev;
      });
    }
  }, [editingPlan, initializeMealSlots]);

  const resetForm = () => {
    const defaultTargets = { calories: 2000, protein: 150, carbs: 200, fat: 60 };
    setFormData({
      mode: "flexible",
      daily_targets: defaultTargets,
      meal_slots: initializeMealSlots(defaultTargets),
      tolerance_percent: 5,
      notes: "",
    });
    setSelectedMember(null);
    setEditingPlan(null);
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setSelectedMember(members.find(m => m.id === plan.user_id) || null);
      setFormData({
        mode: plan.mode || "flexible",
        daily_targets: plan.daily_targets || { calories: 2000, protein: 150, carbs: 200, fat: 60 },
        meal_slots: plan.meal_slots || initializeMealSlots(plan.daily_targets),
        tolerance_percent: plan.tolerance_percent || 5,
        notes: plan.notes || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleDailyTargetChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    const newTargets = { ...formData.daily_targets, [field]: numValue };
    
    // Recalculate meal slot targets proportionally
    const newMealSlots = { ...formData.meal_slots };
    Object.keys(newMealSlots).forEach(slotId => {
      const slot = MEAL_SLOTS.find(s => s.id === slotId);
      if (slot) {
        const pct = slot.defaultPercent / 100;
        newMealSlots[slotId] = {
          ...newMealSlots[slotId],
          targets: {
            ...newMealSlots[slotId].targets,
            [field]: Math.round(numValue * pct),
          },
        };
      }
    });

    setFormData(prev => ({
      ...prev,
      daily_targets: newTargets,
      meal_slots: newMealSlots,
    }));
  };

  const handleMealSlotChange = (slotId, field, value) => {
    setFormData(prev => ({
      ...prev,
      meal_slots: {
        ...prev.meal_slots,
        [slotId]: {
          ...prev.meal_slots[slotId],
          [field]: field === "mode" ? value : { 
            ...prev.meal_slots[slotId]?.targets,
            [field]: parseInt(value) || 0 
          },
        },
      },
    }));
  };

  const handleMealSlotModeChange = (slotId, mode) => {
    setFormData(prev => ({
      ...prev,
      meal_slots: {
        ...prev.meal_slots,
        [slotId]: {
          ...prev.meal_slots[slotId],
          mode: mode,
        },
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedMember && !editingPlan) {
      toast.error("Selecciona un miembro");
      return;
    }

    // Validate daily targets
    const dt = formData.daily_targets;
    if (!dt.calories || !dt.protein || !dt.carbs || !dt.fat) {
      toast.error("Completa todos los macros diarios");
      return;
    }

    setSaving(true);
    try {
      const memberId = editingPlan ? editingPlan.user_id : selectedMember.id;
      
      const payload = {
        mode: formData.mode,
        daily_targets: formData.daily_targets,
        meal_slots: formData.meal_slots,
        tolerance_percent: formData.tolerance_percent,
        notes: formData.notes,
      };

      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/admin/members/${memberId}/flexible-plan`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Plan flexible actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/admin/members/${memberId}/flexible-plan`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Plan flexible creado");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error(error.response?.data?.detail || "Error al guardar plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`¿Eliminar plan flexible de ${plan.member_email}?`)) return;
    
    setDeleting(plan.id);
    try {
      await axios.delete(
        `${API_URL}/api/admin/members/${plan.user_id}/flexible-plan`,
        { headers: getAuthHeaders() }
      );
      toast.success("Plan flexible eliminado");
      fetchData();
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Error al eliminar plan");
    } finally {
      setDeleting(null);
    }
  };

  // Filter members who don't have a flexible plan yet
  const availableMembers = members.filter(
    m => m.is_premium && !plans.some(p => p.user_id === m.id)
  );

  const filteredMembers = availableMembers.filter(m =>
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-flexible-plans">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            Planes Flexibles
          </h1>
          <p className="text-sm text-[#666] mt-1">
            Sistema de recetas por macros - {plans.length} planes activos
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold"
          data-testid="create-flexible-plan-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Plan Flexible
        </Button>
      </div>

      {/* Plans List */}
      {plans.length === 0 ? (
        <Card className="bg-[#111] border-[#1A1A1A]">
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Sin planes flexibles</h3>
            <p className="text-[#666] text-sm mb-4">
              Crea el primer plan flexible para un cliente premium
            </p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {plans.map((plan) => {
            const isExpanded = expandedPlan === plan.id;
            
            return (
              <Card 
                key={plan.id} 
                className="bg-[#111] border-[#1A1A1A] overflow-hidden"
                data-testid={`plan-card-${plan.user_id}`}
              >
                <CardContent className="p-0">
                  {/* Plan Header */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#1A1A1A]/50 transition-colors"
                    onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7355] flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-black" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white truncate">
                          {plan.member_name || plan.member_email}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge 
                            variant="outline" 
                            className={cn(
                              "text-xs",
                              plan.mode === "flexible" 
                                ? "border-green-500/50 text-green-400"
                                : "border-yellow-500/50 text-yellow-400"
                            )}
                          >
                            {plan.mode === "flexible" ? "100% Flexible" : "Híbrido"}
                          </Badge>
                          <span className="text-xs text-[#666]">
                            {plan.daily_targets?.calories} kcal/día
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleOpenDialog(plan); }}
                        className="text-[#666] hover:text-[#D4AF37]"
                        data-testid={`edit-plan-${plan.user_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(plan); }}
                        disabled={deleting === plan.id}
                        className="text-[#666] hover:text-red-400"
                        data-testid={`delete-plan-${plan.user_id}`}
                      >
                        {deleting === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-[#666]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#666]" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-[#1A1A1A] p-4 space-y-4 bg-[#0A0A0A]">
                      {/* Daily Targets */}
                      <div>
                        <h4 className="text-sm font-medium text-[#888] mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Objetivos Diarios
                        </h4>
                        <div className="grid grid-cols-4 gap-2">
                          <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
                            <div className="text-lg font-bold text-[#D4AF37]">
                              {plan.daily_targets?.calories}
                            </div>
                            <div className="text-xs text-[#666]">kcal</div>
                          </div>
                          <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
                            <div className="text-lg font-bold text-red-400">
                              {plan.daily_targets?.protein}g
                            </div>
                            <div className="text-xs text-[#666]">Proteína</div>
                          </div>
                          <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
                            <div className="text-lg font-bold text-yellow-400">
                              {plan.daily_targets?.carbs}g
                            </div>
                            <div className="text-xs text-[#666]">Carbos</div>
                          </div>
                          <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
                            <div className="text-lg font-bold text-blue-400">
                              {plan.daily_targets?.fat}g
                            </div>
                            <div className="text-xs text-[#666]">Grasas</div>
                          </div>
                        </div>
                      </div>

                      {/* Meal Slots Distribution */}
                      <div>
                        <h4 className="text-sm font-medium text-[#888] mb-3 flex items-center gap-2">
                          <UtensilsCrossed className="w-4 h-4" />
                          Distribución por Comidas
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(plan.meal_slots || {}).map(([slotId, slot]) => (
                            <div 
                              key={slotId}
                              className="flex items-center justify-between bg-[#1A1A1A] rounded-lg px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-white">
                                  {slot.name || slotId}
                                </span>
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-[10px]",
                                    slot.mode === "flexible" 
                                      ? "border-green-500/30 text-green-400"
                                      : "border-orange-500/30 text-orange-400"
                                  )}
                                >
                                  {slot.mode === "flexible" ? "Flexible" : "Fijo"}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-[#666]">
                                <span>{slot.targets?.calories} kcal</span>
                                <span className="text-red-400">{slot.targets?.protein}P</span>
                                <span className="text-yellow-400">{slot.targets?.carbs}C</span>
                                <span className="text-blue-400">{slot.targets?.fat}G</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      {plan.notes && (
                        <div className="text-sm text-[#666] bg-[#1A1A1A] rounded-lg p-3">
                          <span className="font-medium text-[#888]">Notas:</span> {plan.notes}
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="text-xs text-[#555] flex items-center gap-4">
                        <span>Tolerancia: ±{plan.tolerance_percent}%</span>
                        <span>Creado: {new Date(plan.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111] border-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#D4AF37]" />
              {editingPlan ? "Editar Plan Flexible" : "Nuevo Plan Flexible"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Member Selection (only for new plans) */}
            {!editingPlan && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#888]">
                  Cliente Premium
                </Label>
                {selectedMember ? (
                  <div className="flex items-center justify-between bg-[#1A1A1A] rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {selectedMember.name || selectedMember.email}
                        </div>
                        <div className="text-xs text-[#666]">{selectedMember.email}</div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedMember(null)}
                      className="text-[#666] hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                      <Input
                        placeholder="Buscar cliente premium..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                        data-testid="member-search-input"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-lg p-2">
                      {filteredMembers.length === 0 ? (
                        <p className="text-sm text-[#666] text-center py-4">
                          {availableMembers.length === 0 
                            ? "Todos los clientes premium ya tienen plan flexible"
                            : "No se encontraron clientes"
                          }
                        </p>
                      ) : (
                        filteredMembers.map(member => (
                          <button
                            key={member.id}
                            onClick={() => { setSelectedMember(member); setSearchQuery(""); }}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A1A1A] transition-colors text-left"
                            data-testid={`select-member-${member.id}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                              <Users className="w-4 h-4 text-[#666]" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm text-white truncate">
                                {member.name || member.email}
                              </div>
                              <div className="text-xs text-[#555] truncate">{member.email}</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mode Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#888]">Modo del Plan</Label>
              <Select
                value={formData.mode}
                onValueChange={(value) => setFormData(prev => ({ ...prev, mode: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {MODE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-white">
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-[#666]">{opt.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Daily Targets */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-[#888] flex items-center gap-2">
                <Target className="w-4 h-4" />
                Macros Diarios
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#666]">Calorías</Label>
                  <div className="relative">
                    <Flame className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                    <Input
                      type="number"
                      value={formData.daily_targets.calories}
                      onChange={(e) => handleDailyTargetChange("calories", e.target.value)}
                      className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                      data-testid="daily-calories-input"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#666]">Proteína (g)</Label>
                  <Input
                    type="number"
                    value={formData.daily_targets.protein}
                    onChange={(e) => handleDailyTargetChange("protein", e.target.value)}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-red-400"
                    data-testid="daily-protein-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#666]">Carbos (g)</Label>
                  <Input
                    type="number"
                    value={formData.daily_targets.carbs}
                    onChange={(e) => handleDailyTargetChange("carbs", e.target.value)}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-yellow-400"
                    data-testid="daily-carbs-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#666]">Grasas (g)</Label>
                  <Input
                    type="number"
                    value={formData.daily_targets.fat}
                    onChange={(e) => handleDailyTargetChange("fat", e.target.value)}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-blue-400"
                    data-testid="daily-fat-input"
                  />
                </div>
              </div>
            </div>

            {/* Meal Slots Configuration */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-[#888] flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4" />
                Distribución por Comidas
              </Label>
              <div className="space-y-2">
                {MEAL_SLOTS.map(slot => {
                  const slotData = formData.meal_slots[slot.id] || {};
                  const slotTargets = slotData.targets || {};
                  
                  return (
                    <Card key={slot.id} className="bg-[#0A0A0A] border-[#1A1A1A]">
                      <CardContent className="p-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          {/* Slot name and mode toggle */}
                          <div className="flex items-center justify-between sm:w-40">
                            <span className="text-sm font-medium text-white">{slot.name}</span>
                            {formData.mode === "hybrid" && (
                              <button
                                type="button"
                                onClick={() => handleMealSlotModeChange(
                                  slot.id, 
                                  slotData.mode === "flexible" ? "fixed" : "flexible"
                                )}
                                className={cn(
                                  "px-2 py-1 rounded text-xs font-medium transition-colors",
                                  slotData.mode === "flexible"
                                    ? "bg-green-500/20 text-green-400"
                                    : "bg-orange-500/20 text-orange-400"
                                )}
                                data-testid={`toggle-mode-${slot.id}`}
                              >
                                {slotData.mode === "flexible" ? "Flex" : "Fijo"}
                              </button>
                            )}
                          </div>
                          
                          {/* Macro inputs */}
                          <div className="flex-1 grid grid-cols-4 gap-2">
                            <Input
                              type="number"
                              placeholder="kcal"
                              value={slotTargets.calories || ""}
                              onChange={(e) => handleMealSlotChange(slot.id, "calories", e.target.value)}
                              className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-sm h-9"
                              data-testid={`slot-calories-${slot.id}`}
                            />
                            <Input
                              type="number"
                              placeholder="P"
                              value={slotTargets.protein || ""}
                              onChange={(e) => handleMealSlotChange(slot.id, "protein", e.target.value)}
                              className="bg-[#1A1A1A] border-[#2A2A2A] text-red-400 text-sm h-9"
                            />
                            <Input
                              type="number"
                              placeholder="C"
                              value={slotTargets.carbs || ""}
                              onChange={(e) => handleMealSlotChange(slot.id, "carbs", e.target.value)}
                              className="bg-[#1A1A1A] border-[#2A2A2A] text-yellow-400 text-sm h-9"
                            />
                            <Input
                              type="number"
                              placeholder="G"
                              value={slotTargets.fat || ""}
                              onChange={(e) => handleMealSlotChange(slot.id, "fat", e.target.value)}
                              className="bg-[#1A1A1A] border-[#2A2A2A] text-blue-400 text-sm h-9"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Tolerance */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#888]">
                Tolerancia de Macros (±%)
              </Label>
              <Select
                value={String(formData.tolerance_percent)}
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  tolerance_percent: parseInt(value) 
                }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {[3, 5, 8, 10, 15].map(val => (
                    <SelectItem key={val} value={String(val)} className="text-white">
                      ±{val}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#888]">Notas (opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas para el cliente o recordatorios..."
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white resize-none"
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#1A1A1A]">
              <Button
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-[#666] hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || (!selectedMember && !editingPlan)}
                className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold"
                data-testid="save-flexible-plan-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingPlan ? "Actualizar" : "Crear Plan"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFlexiblePlans;
